import { useState, useEffect, useMemo, forwardRef } from "react";
import { observer } from "mobx-react";

import { Box, CircularProgress } from "@mui/material";

import { PAYERS, STATE_CODE_TO_NAME, LOB_VALUE_TO_NAME } from "../../config";
import { apiService, Payer } from "../../services";
import Column from "../ui/core/Column";
import { useAppStore } from "../../hooks/useAppStore";
import { useChatCategoryStore } from "../../hooks/useChatCategoryStore";
import { PreChatsHelper } from "../helpers/preChatsHelper";
import { ContractResearchFilters } from "./contract-research/ContractResearchFilters";
import { ContractResearchTable } from "./contract-research/ContractResearchTable";
import ContractWelcomeContent from "./contract-research/ContractWelcomeContent";
import ChatInput from "../chat/components/ChatInput";

export interface ContractFormData {
  payer: string;
  state: string;
  lineOfBusiness: string;
}

interface ContractProps {
  onSubmit?: (data: ContractFormData) => void;
}

export interface ContractResearchRef {
  getFormData: () => ContractFormData | null;
  isFormComplete: () => boolean;
}

const ContractResearch = forwardRef<ContractResearchRef, ContractProps>(() => {
  const { setContractToPreview } = useAppStore();
  const {
    filters: formData,
    setContractResearchFilters,
    isContractsDrawerOpen,
    isLoadingContracts,
    setContractsData,
    fetchContracts,
    isAskingReveForContract,
    setIsAskingReveForContract,
    clearChatCategoryStore,
    restoreContractResearchFilters,
  } = useChatCategoryStore();

  const [payersData, setPayersData] = useState<Payer[]>([]);
  const [isLoadingPayers, setIsLoadingPayers] = useState(true);

  // Fetch payers with details on component mount
  useEffect(() => {
    const fetchPayers = async () => {
      try {
        setIsLoadingPayers(true);
        const payersResponse = await apiService.getContractPayers();

        if (payersResponse.success && payersResponse.data) {
          setPayersData(payersResponse.data);
        } else {
          console.error(
            "Failed to fetch payers options:",
            payersResponse.error,
          );
          // Fallback to hardcoded states with all payers and LOBs
          const fallbackPayers: Payer[] = Object.entries(
            STATE_CODE_TO_NAME,
          ).map(([code, name]) => ({
            code: code,
            name: name,
            payers: Object.entries(PAYERS).map(([apiName, displayName]) => ({
              name: apiName,
              lines_of_business: Object.keys(LOB_VALUE_TO_NAME),
            })),
          }));
          setPayersData(fallbackPayers);
        }
      } catch (error) {
        console.error("Error fetching payers options:", error);
        // Fallback to hardcoded states with all payers and LOBs
        const fallbackPayers: Payer[] = Object.entries(STATE_CODE_TO_NAME).map(
          ([code, name]) => ({
            code: code,
            name: name,
            payers: Object.entries(PAYERS).map(([apiName, displayName]) => ({
              name: apiName,
              lines_of_business: Object.keys(LOB_VALUE_TO_NAME),
            })),
          }),
        );
        setPayersData(fallbackPayers);
      } finally {
        setIsLoadingPayers(false);
      }
    };

    // Start fresh when this mounts, then fetch — must happen in this effect
    // rather than the page's, since child effects run before parent effects on
    // mount; clearing here first (not after) keeps the initial fetchContracts()
    // from running against filters/state a previous visit left behind, and from
    // being immediately wiped out by a parent-level clear once that ran later.
    clearChatCategoryStore(true);
    // Then re-apply the user's saved filters (localStorage "filters:contracts").
    restoreContractResearchFilters();
    fetchPayers();
    fetchContracts();
  }, []);

  // Get selected state data
  const selectedState = useMemo(() => {
    return payersData.find(
      (state) => state.name === formData.state || state.code === formData.state,
    );
  }, [payersData, formData.state]);

  const handleChange = (field: keyof ContractFormData, value: string) => {
    if (field === "state" || field === "payer") {
      setContractToPreview(null);
    }

    let updatedForm: Partial<ContractFormData> = {};

    // If state changes, reset dependent fields (payer and LOB)
    if (field === "state") {
      // Find the state data for the newly selected state
      const newSelectedState = payersData.find(
        (state) => state.name === value || state.code === value,
      );
      const newPayersOptions = PreChatsHelper.extractPayerOptions(
        value,
        newSelectedState,
        payersData,
      );

      // If there's only one payer for this state, auto-select it
      if (newPayersOptions.length === 1) {
        const singlePayerName = newPayersOptions[0].value;
        const singlePayer = newSelectedState?.payers?.find(
          (p) => p.name === singlePayerName,
        );
        const linesOfBusiness = singlePayer?.lines_of_business || [];

        // If there's also only one line of business, auto-select it too
        if (linesOfBusiness.length === 1) {
          updatedForm = {
            state: value,
            payer: singlePayerName,
            lineOfBusiness: linesOfBusiness[0],
          };
        } else {
          updatedForm = {
            state: value,
            payer: singlePayerName,
            lineOfBusiness: "",
          };
        }
      } else {
        updatedForm = { state: value, payer: "", lineOfBusiness: "" };
        setContractsData({ total: 0, contracts: [] });
      }
    }
    // If payer changes, reset dependent fields (LOB only)
    else if (field === "payer") {
      const payer = selectedState?.payers?.find((p) => p.name === value);
      const linesOfBusiness = payer?.lines_of_business || [];

      // If there's only one line of business, auto-select it
      if (linesOfBusiness.length === 1) {
        updatedForm = { payer: value, lineOfBusiness: linesOfBusiness[0] };
      } else {
        updatedForm = { payer: value, lineOfBusiness: "" };
      }
    } else {
      updatedForm = { [field]: value };
    }

    setContractResearchFilters(updatedForm);
    fetchContracts();
  };

  return (
    <Column
      sx={{
        alignItems: "center",
        justifyContent: "flex-start",
        width: "100%",
        minWidth: 0,
        height: isAskingReveForContract || isLoadingPayers ? "100%" : "auto",
        minHeight: 0,
      }}
    >
      {isLoadingPayers ? (
        <Column
          sx={{
            width: "100%",
            height: "100%",
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size="27px" />
        </Column>
      ) : (
        <>
          {/* Keep the filters/table mounted while the Ask Reve welcome flow shows —
              unmounting would refetch contracts on every open/cancel. */}
          <Box
            sx={{ display: isAskingReveForContract ? "none" : "contents" }}
          >
            <ContractResearchFilters
              payersData={payersData}
              onFormChange={handleChange}
            />

            <ContractResearchTable />
          </Box>

          {isAskingReveForContract ? (
            <Column sx={{ height: "100%", width: "100%", minHeight: 0 }}>
              <ContractWelcomeContent
                onClose={() => setIsAskingReveForContract(false)}
              />
              <ChatInput forceEnable />
            </Column>
          ) : null}
        </>
      )}
    </Column>
  );
});

ContractResearch.displayName = "ContractResearch";

export default observer(ContractResearch);
